export const COUNTRY_SHORTHANDS = {
  UNITED_STATES: 'US',
  CANADA: 'CA',
  UNITED_KINGDOM: 'GB',
} as const;

export type CountryShorthand = (typeof COUNTRY_SHORTHANDS)[keyof typeof COUNTRY_SHORTHANDS];

export type CountriesWithStates = 'US' | 'CA';

export interface StateNaming<T, U> {
  name: T;
  shorthand: U;
}

export type States<T, U extends string> = {
  [key in U]: StateNaming<T, U>;
};

export type StateShorthand = UnitedStates.StateShorthand | Canada.StateShorthand;
export type StateName = UnitedStates.StateName | Canada.StateName;
export type State = StateNaming<StateName, StateShorthand>;

export namespace UnitedStates {
  export type StateShorthand =
    | 'AL'
    | 'AK'
    | 'AZ'
    | 'AR'
    | 'CA'
    | 'CO'
    | 'CT'
    | 'DE'
    | 'DC'
    | 'FL'
    | 'GA'
    | 'HI'
    | 'ID'
    | 'IL'
    | 'IN'
    | 'IA'
    | 'KS'
    | 'KY'
    | 'LA'
    | 'ME'
    | 'MD'
    | 'MA'
    | 'MI'
    | 'MN'
    | 'MS'
    | 'MO'
    | 'MT'
    | 'NE'
    | 'NV'
    | 'NH'
    | 'NJ'
    | 'NM'
    | 'NY'
    | 'NC'
    | 'ND'
    | 'OH'
    | 'OK'
    | 'OR'
    | 'PA'
    | 'RI'
    | 'SC'
    | 'SD'
    | 'TN'
    | 'TX'
    | 'UT'
    | 'VT'
    | 'VA'
    | 'WA'
    | 'WV'
    | 'WI'
    | 'WY';

  export type StateName =
    | 'Alabama'
    | 'Alaska'
    | 'Arizona'
    | 'Arkansas'
    | 'California'
    | 'Colorado'
    | 'Connecticut'
    | 'Delaware'
    | 'District of Columbia'
    | 'Florida'
    | 'Georgia'
    | 'Hawaii'
    | 'Idaho'
    | 'Illinois'
    | 'Indiana'
    | 'Iowa'
    | 'Kansas'
    | 'Kentucky'
    | 'Louisiana'
    | 'Maine'
    | 'Maryland'
    | 'Massachusetts'
    | 'Michigan'
    | 'Minnesota'
    | 'Mississippi'
    | 'Missouri'
    | 'Montana'
    | 'Nebraska'
    | 'Nevada'
    | 'New Hampshire'
    | 'New Jersey'
    | 'New Mexico'
    | 'New York'
    | 'North Carolina'
    | 'North Dakota'
    | 'Ohio'
    | 'Oklahoma'
    | 'Oregon'
    | 'Pennsylvania'
    | 'Rhode Island'
    | 'South Carolina'
    | 'South Dakota'
    | 'Tennessee'
    | 'Texas'
    | 'Utah'
    | 'Vermont'
    | 'Virginia'
    | 'Washington'
    | 'West Virginia'
    | 'Wisconsin'
    | 'Wyoming';
}

export namespace Canada {
  export type StateShorthand =
    | 'AB'
    | 'BC'
    | 'MB'
    | 'NB'
    | 'NL'
    | 'NT'
    | 'NS'
    | 'NU'
    | 'ON'
    | 'PE'
    | 'QC'
    | 'SK'
    | 'YT';

  export type StateName =
    | 'Alberta'
    | 'British Columbia'
    | 'Manitoba'
    | 'New Brunswick'
    | 'Newfoundland and Labrador'
    | 'Northwest Territories'
    | 'Nova Scotia'
    | 'Nunavut'
    | 'Ontario'
    | 'Prince Edward Island'
    | 'Quebec'
    | 'Saskatchewan'
    | 'Yukon';
}

/**
 * Variables
 */
export const UNITED_STATES: {
  states: States<UnitedStates.StateName, UnitedStates.StateShorthand>;
} = {
  states: {
    AL: { name: 'Alabama', shorthand: 'AL' },
    AK: { name: 'Alaska', shorthand: 'AK' },
    AZ: { name: 'Arizona', shorthand: 'AZ' },
    AR: { name: 'Arkansas', shorthand: 'AR' },
    CA: { name: 'California', shorthand: 'CA' },
    CO: { name: 'Colorado', shorthand: 'CO' },
    CT: { name: 'Connecticut', shorthand: 'CT' },
    DE: { name: 'Delaware', shorthand: 'DE' },
    DC: { name: 'District of Columbia', shorthand: 'DC' },
    FL: { name: 'Florida', shorthand: 'FL' },
    GA: { name: 'Georgia', shorthand: 'GA' },
    HI: { name: 'Hawaii', shorthand: 'HI' },
    ID: { name: 'Idaho', shorthand: 'ID' },
    IL: { name: 'Illinois', shorthand: 'IL' },
    IN: { name: 'Indiana', shorthand: 'IN' },
    IA: { name: 'Iowa', shorthand: 'IA' },
    KS: { name: 'Kansas', shorthand: 'KS' },
    KY: { name: 'Kentucky', shorthand: 'KY' },
    LA: { name: 'Louisiana', shorthand: 'LA' },
    ME: { name: 'Maine', shorthand: 'ME' },
    MD: { name: 'Maryland', shorthand: 'MD' },
    MA: { name: 'Massachusetts', shorthand: 'MA' },
    MI: { name: 'Michigan', shorthand: 'MI' },
    MN: { name: 'Minnesota', shorthand: 'MN' },
    MS: { name: 'Mississippi', shorthand: 'MS' },
    MO: { name: 'Missouri', shorthand: 'MO' },
    MT: { name: 'Montana', shorthand: 'MT' },
    NE: { name: 'Nebraska', shorthand: 'NE' },
    NV: { name: 'Nevada', shorthand: 'NV' },
    NH: { name: 'New Hampshire', shorthand: 'NH' },
    NJ: { name: 'New Jersey', shorthand: 'NJ' },
    NM: { name: 'New Mexico', shorthand: 'NM' },
    NY: { name: 'New York', shorthand: 'NY' },
    NC: { name: 'North Carolina', shorthand: 'NC' },
    ND: { name: 'North Dakota', shorthand: 'ND' },
    OH: { name: 'Ohio', shorthand: 'OH' },
    OK: { name: 'Oklahoma', shorthand: 'OK' },
    OR: { name: 'Oregon', shorthand: 'OR' },
    PA: { name: 'Pennsylvania', shorthand: 'PA' },
    RI: { name: 'Rhode Island', shorthand: 'RI' },
    SC: { name: 'South Carolina', shorthand: 'SC' },
    SD: { name: 'South Dakota', shorthand: 'SD' },
    TN: { name: 'Tennessee', shorthand: 'TN' },
    TX: { name: 'Texas', shorthand: 'TX' },
    UT: { name: 'Utah', shorthand: 'UT' },
    VT: { name: 'Vermont', shorthand: 'VT' },
    VA: { name: 'Virginia', shorthand: 'VA' },
    WA: { name: 'Washington', shorthand: 'WA' },
    WV: { name: 'West Virginia', shorthand: 'WV' },
    WI: { name: 'Wisconsin', shorthand: 'WI' },
    WY: { name: 'Wyoming', shorthand: 'WY' },
  },
};

export const CANADA: { states: States<Canada.StateName, Canada.StateShorthand> } = {
  states: {
    AB: { name: 'Alberta', shorthand: 'AB' },
    BC: { name: 'British Columbia', shorthand: 'BC' },
    MB: { name: 'Manitoba', shorthand: 'MB' },
    NB: { name: 'New Brunswick', shorthand: 'NB' },
    NL: { name: 'Newfoundland and Labrador', shorthand: 'NL' },
    NT: { name: 'Northwest Territories', shorthand: 'NT' },
    NS: { name: 'Nova Scotia', shorthand: 'NS' },
    NU: { name: 'Nunavut', shorthand: 'NU' },
    ON: { name: 'Ontario', shorthand: 'ON' },
    PE: { name: 'Prince Edward Island', shorthand: 'PE' },
    QC: { name: 'Quebec', shorthand: 'QC' },
    SK: { name: 'Saskatchewan', shorthand: 'SK' },
    YT: { name: 'Yukon', shorthand: 'YT' },
  },
};
